'use client';

import type { TargetType } from './constants';
import { type FC } from 'react';

import { cn, IconButton } from '@yanolja-rnd/nol-design-system';
import { IcClose } from '@yanolja-rnd/nol-icons';
import Modal from 'react-modal';
import { useAtomValue } from 'jotai';
import { deviceAtom } from '@/app/_shared/store/device';
import DotLoader from '@/app/_components/common/dot-loader';
import { useRegionAccommodationQuery } from '../api';
import { TARGET_TO_REGION_TYPE } from './constants';
import { RegionSelectArea } from './region-select-area';
import { ErrorFallback } from './error-fallback';

interface RecentSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: string;
}

export const RecentSelectModal: FC<RecentSelectModalProps> = ({ isOpen, onClose, targetType }) => {
  const { deviceType } = useAtomValue(deviceAtom);

  const regionType = TARGET_TO_REGION_TYPE[targetType as TargetType] || TARGET_TO_REGION_TYPE.DOMESTIC_MOTEL;

  const { data: regionList, isLoading, refetch } = useRegionAccommodationQuery({ type: regionType });

  const isNoContentsOrFailover =
    (!regionList?.items || (!!regionList?.items && regionList.items.length < 1)) && !isLoading;

  return (
    <Modal
      ariaHideApp={false}
      closeTimeoutMS={210}
      isOpen={isOpen}
      className={{
        afterOpen: 'animate-modalOpen',
        base: 'h-full w-full translate-y-[100%]',
        beforeClose: '!animate-modalClose',
      }}
      overlayClassName={{
        afterOpen: 'animate-overlayOpen',
        base: 'fixed opacity-0 inset-0 bg-scrim z-high left-1/2 -translate-x-1/2 max-w-mobile-size pc:max-w-[none] w-full',
        beforeClose: '!animate-overlayClose',
      }}
      onRequestClose={onClose}>
      <div className="flex h-full flex-col bg-static-white">
        <header className="z-[1] min-h-48 border-b border-line-neutral-weak1 pl-6 pr-16">
          <div className="relative mx-auto my-0 flex items-center pc:w-[132.4rem]">
            <div className="absolute">
              <IconButton icon={<IcClose />} size="medium" theme="ghost" onClick={onClose} />
            </div>
            <h1
              className={cn('mb-10 mt-13 block w-full text-center typography-subtitle-18-bold', {
                'ml-40 text-left': deviceType === 'android',
              })}>
              지역 선택
            </h1>
          </div>
        </header>

        <div className="w-full flex-auto overflow-auto">
          {(!!isLoading || !!isNoContentsOrFailover) && (
            <div className="flex h-full items-center justify-center">
              {!!isLoading && <DotLoader />}
              {!!isNoContentsOrFailover && <ErrorFallback refetch={refetch} />}
            </div>
          )}
          {!!regionList?.items && (
            <RegionSelectArea targetType={targetType} regionItems={regionList.items} regionType={regionType} />
          )}
        </div>
      </div>
    </Modal>
  );
};
